import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import { LocalizationContext } from 'contexts/LocalizationContext/LocalizationContext';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { GraphChart } from 'shared/components/GraphChart/GraphChart';
import { BaseSection } from 'shared/sections/BaseSection/BaseSection';
import { SectionTitle } from 'shared/sections/SectionTitle/SectionTitle';
import { SectionCTA } from 'shared/components/SectionCTA/SectionCTA';
import { FadeInOnScroll } from 'shared/components/FadeInOnScroll/FadeInOnScroll';
import { data } from 'data/techStack.data';
import { MenuItemElement } from 'contexts/LayoutContext/LayoutContext.types';

const useVhPixels = (vh: number) => {
  const [px, setPx] = useState(Math.round(window.innerHeight * vh / 100));
  useEffect(() => {
    const onResize = () => setPx(Math.round(window.innerHeight * vh / 100));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [vh]);
  return px;
};

export const TechStackSection = forwardRef<MenuItemElement>((props, ref) => {
  const navigate = useNavigate();
  const {dictionary} = useContext(LocalizationContext);
  const { teamSectionRef } = useContext(LayoutContext);
  const { isMobile } = useContext(LayoutContext);
  const networkInstance = useRef<any>(null);
  const graphHeight = useVhPixels(60);

  const handleZoom = useCallback((direction: 'in' | 'out') => {
    const network = networkInstance.current;
    if (!network) return;
    const currentScale = network.getScale();
    const factor = direction === 'in' ? 1.4 : 0.7;
    const newScale = Math.max(0.1, Math.min(5, currentScale * factor));
    network.moveTo({ scale: newScale, animation: { duration: 300, easingFunction: 'easeInOutQuad' } });
  }, []);

  const handleReset = useCallback(() => {
    if (networkInstance.current) {
      networkInstance.current.fit({ animation: { duration: 400, easingFunction: 'easeInOutQuad' } });
    }
  }, []);

  const btnSx = {
    bgcolor: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' },
  };

  return (
    <BaseSection ref={ref} style={{ background: '#000', position: 'relative', zIndex: 1 }}>
      <FadeInOnScroll>
        <SectionTitle onEnterViewport={() => navigate('/tech-stack')} color="#fff">
          {dictionary.techStackSectionTitle}
        </SectionTitle>
      </FadeInOnScroll>
      <br/>
      <Typography
        variant="body1"
        fontSize={isMobile ? 13 : 16}
        sx={{
          px: isMobile ? 2 : 2,
          mx: 'auto',
          color: 'rgba(255,255,255,0.6)',
        }}
      >
        {dictionary.techStackSectionSubtitle}
      </Typography>
      <br/>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
        <Tooltip title="Zoom out" arrow>
          <IconButton size="small" onClick={() => handleZoom('out')} sx={btnSx}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view" arrow>
          <IconButton size="small" onClick={handleReset} sx={btnSx}>
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in" arrow>
          <IconButton size="small" onClick={() => handleZoom('in')} sx={btnSx}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <div style={{
        width: '100%',
        height: graphHeight,
        marginBottom: 0,
        position: 'relative',
      }}>
        {/* Top gradient fade */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          background: 'linear-gradient(to bottom, #000 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />
        {/* Bottom gradient fade */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 60,
          background: 'linear-gradient(to top, #000 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />
        {/* Left gradient fade */}
        <div style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 60,
          background: 'linear-gradient(to right, #000 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />
        {/* Right gradient fade */}
        <div style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: 60,
          background: 'linear-gradient(to left, #000 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />
        <GraphChart
          data={data}
          compact={isMobile}
          forceDark
          onNetworkReady={(network: any) => { networkInstance.current = network; }}
        />
      </div>
      <SectionCTA
        nextSectionLabel="Our Team"
        onNextSection={() => teamSectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}
      />
    </BaseSection>
  );
});
